import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ScaffoldDialogRef } from '@app/framework/dialog/scaffold-dialog.service';
import { EnvironmentService } from '@services/environment.service';
import { HttpUtilService } from '@services/http-util.service';
import { NotificationsService } from '@services/notifications.service';
import { TranslationService } from '@services/translation.service';
import { UserService } from '@services/user.service';
import { of, throwError } from 'rxjs';
import { catchError, shareReplay, switchMap, take, tap } from 'rxjs/operators';

export interface MfaModalDialogData {
  initialView?: 'initial' | 'mfa';
  requireSetup?: boolean;
}

@Component({
  selector: 'app-mfa-modal',
  templateUrl: './mfa-modal.component.html',
  styleUrls: ['./mfa-modal.component.scss'],
  standalone: false,
})
export class MfaModalComponent implements OnInit {
  @Input() data?: MfaModalDialogData;

  public isLoading: boolean;
  public isSubmitting: boolean;
  public secretCode: string;
  public totbCodeControl = new FormControl('');
  public totbVerified: boolean = false;
  public userInfo;
  public userInfo$;
  public view: 'initial' | 'mfa' = 'initial';
  public mfaSetupLoadFailed = false;
  public requireSetup = false;

  constructor(
    private environmentService: EnvironmentService,
    public dialogRef: ScaffoldDialogRef,
    private http: HttpUtilService,
    public notifications: NotificationsService,
    private userService: UserService,
    private translation: TranslationService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncDialogConfig();

    this.userInfo$ = this.userService
      .getRoleDetails()
      .pipe(
        catchError(error => of({ error })),
        tap((role: any) => {
          if (role?.data) {
            this.userInfo = role.data;
          }
        }),
        shareReplay(1)
      );
    this.userInfo$.pipe(take(1)).subscribe();
  }

  private readDialogData(): MfaModalDialogData {
    return this.data ?? this.dialogRef?.settings?.context?.data ?? {};
  }

  /**
   * Called after open from UserService so required-setup config applies before first paint.
   */
  syncDialogConfig(data?: MfaModalDialogData): void {
    if (data) {
      this.data = data;
    }
    this.applyDialogData(this.readDialogData());
  }

  private applyDialogData(config: MfaModalDialogData): void {
    this.requireSetup = config.requireSetup === true;
    const initialView = config.initialView;
    if (initialView === 'mfa') {
      this.view = 'mfa';
      if (!this.isLoading && !this.secretCode) {
        this.initTotbMfaSetup();
      }
    } else if (initialView === 'initial') {
      this.view = 'initial';
    }
    this.cd.markForCheck();
  }

  close() {
    if (this.requireSetup) {
      return;
    }
    this.dialogRef.close();
  }

  handleDialogClose() {
    this.close();
  }

  showMfaSetup() {
    this.view = 'mfa';
    this.initTotbMfaSetup();
    this.cd.detectChanges();
  }

  initTotbMfaSetup() {
    this.isLoading = true;
    this.totbCodeControl.setValue('');
    this.mfaSetupLoadFailed = false;
    this.secretCode = undefined;
    this.cd.markForCheck();
    this.http
      .request(
        'get',
        `${this.environmentService.environment.apiRoot}/auth/inittotbmfa`
      )
      .pipe(catchError(error => of({ error })))
      .subscribe(response => {
        this.isLoading = false;
        if (response?.error) {
          this.mfaSetupLoadFailed = true;
          this.notifications.popNotification(
            '',
            this.translation.getTranslatedContent(
              'mfa_setup_load_failed',
              'Unable to load MFA setup. Please try again.'
            )
          );
          this.cd.detectChanges();
          return;
        }
        this.mfaSetupLoadFailed = false;
        this.secretCode = response.SecretCode;
        this.view = 'mfa';
        this.cd.detectChanges();
      });
  }

  private getCognitoUserForUpdateMfa(role: any): {
    username: string;
    id: string;
  } | null {
    const fromRoleData = role?.data;
    const username =
      fromRoleData?.cognito_username ??
      role?.cognito_username ??
      this.userInfo?.cognito_username ??
      this.userInfo?.username;
    const id =
      fromRoleData?.user_id ??
      role?.user_id ??
      this.userInfo?.user_id;
    if (username == null || id == null || username === '' || id === '') {
      return null;
    }
    return { username: String(username), id: String(id) };
  }

  handleSuccessfulTotbVerifyResponse() {
    this.userService
      .getRoleDetails()
      .pipe(
        take(1),
        switchMap((role: any) => {
          const creds = this.getCognitoUserForUpdateMfa(role);
          if (!creds) {
            return throwError(
              () => new Error('User info not available for MFA update')
            );
          }
          return this.http.request(
            'post',
            `${this.environmentService.environment.apiRoot}/auth/updatemfa`,
            { username: creds.username, id: creds.id, type: 'totb' }
          );
        }),
        catchError(error => of({ error }))
      )
      .subscribe(result => {
        if (result?.error) {
          this.notifications.popNotification(
            '',
            this.translation.getTranslatedContent(
              'unknown_error',
              'Unknown Error'
            )
          );
          return;
        }
        this.userService.updateUser$({ mfa_set: true }).subscribe({
          next: () => {
            const message = this.translation.getTranslatedContent(
              'successfully_verified_for_mfa_with_authenticator_app',
              'Successfully Verified for MFA with Authenticator App'
            );
            this.notifications.popNotification('', message);
            this.totbVerified = true;
            if (this.requireSetup) {
              this.userService.setLoginMfaSetupRequired(false);
            }
            this.dialogRef.close();
          },
          error: () => {
            this.notifications.popNotification(
              '',
              this.translation.getTranslatedContent(
                'unknown_error',
                'Unknown Error'
              )
            );
          },
        });
      });
  }

  handleUnsuccessfulVerifyResponse(response) {
    if (response.error === 'CodeMismatchException')
      return this.notifications.popNotification(
        '',
        this.translation.getTranslatedContent(
          'error_with_code_provided._please_try_again',
          'Error with Code Provided. Please try again.'
        )
      );
    switch (response?.error?.name) {
      case 'CodeMismatchException':
      case 'EnableSoftwareTokenMFAException':
      case 'InvalidParameterException': {
        return this.notifications.popNotification(
          '',
          this.translation.getTranslatedContent(
            'error_with_code_provided._please_try_again',
            'Error with Code Provided. Please try again.'
          )
        );
      }

      default: {
        this.notifications.popNotification(
          '',
          this.translation.getTranslatedContent(
            'unknown_error',
            'Unknown Error'
          )
        );
      }
    }
  }

  submitTotbUsercode($event) {
    $event?.preventDefault();
    this.isSubmitting = true;
    this.http
      .request(
        'post',
        `${this.environmentService.environment.apiRoot}/auth/verifytotbmfa`,
        JSON.stringify({ usercode: this.totbCodeControl.value })
      )
      .pipe(catchError(error => of({ error })))
      .subscribe(raw => {
        this.isSubmitting = false;
        const status = raw?.Status ?? raw?.status;
        const ok =
          typeof status === 'string' && status.toUpperCase() === 'SUCCESS';
        ok
          ? this.handleSuccessfulTotbVerifyResponse()
          : this.handleUnsuccessfulVerifyResponse(raw);
      });
  }
}
